import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MemberStatusClient, MemberStatusDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberStatusClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useMemberStatuses = () => {
    return useQuery({
        queryKey: ['memberStatuses'],
        queryFn: async () => {
            const response = await apiClient.searchMemberStatuses({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const useMemberStatusSearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchMemberStatuses(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const useMemberStatusById = (id: string) => {
    return useQuery({
        queryKey: ['memberStatus', id],
        queryFn: async () => {
            const response = await apiClient.getMemberStatusById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreateMemberStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: MemberStatusDTO) => {
            return await apiClient.createMemberStatus(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberStatuses'] });
        },
    });
};

// Update mutation
export const useUpdateMemberStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: MemberStatusDTO }) => {
            return await apiClient.updateMemberStatus(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberStatuses'] });
        },
    });
};

// Delete mutation
export const useDeleteMemberStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deleteMemberStatus(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberStatuses'] });
        },
    });
};